import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useKnowledge } from '../hooks/useKnowledge'

const STATUS_OPTS = ['all', 'active', 'draft', 'archived'] as const

type StatusFilter = (typeof STATUS_OPTS)[number]

/** List view — same shell / controls / row language as Todos, cached in context. */
export function KnowledgePage() {
  const {
    corpora,
    corpusId,
    docs,
    loading,
    pending,
    error,
    selectCorpus,
    refresh,
    addCorpus,
    addDocument,
  } = useKnowledge()

  const [status, setStatus] = useState<StatusFilter>('all')
  const [query, setQuery] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [ingestOpen, setIngestOpen] = useState(false)
  const [ingestSlug, setIngestSlug] = useState('')
  const [ingestMd, setIngestMd] = useState('')
  const [ingestActivate, setIngestActivate] = useState(true)

  const [newCorpusOpen, setNewCorpusOpen] = useState(false)
  const [newSlug, setNewSlug] = useState('')
  const [newName, setNewName] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return docs.filter((d) => {
      if (status !== 'all' && d.status !== status) return false
      if (!q) return true
      return (
        d.title.toLowerCase().includes(q) ||
        d.slug.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q)
      )
    })
  }, [docs, status, query])

  const activeCount = docs.filter((d) => d.status === 'active').length
  const corpus = corpora.find((c) => c.id === corpusId) ?? null

  const runIngest = async (e: FormEvent) => {
    e.preventDefault()
    if (!ingestSlug.trim() || !ingestMd.trim()) return
    setBusy(true)
    setFormError(null)
    try {
      await addDocument({
        slug: ingestSlug.trim(),
        markdown: ingestMd,
        activate: ingestActivate,
      })
      setIngestSlug('')
      setIngestMd('')
      setIngestOpen(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'ingest_failed')
    } finally {
      setBusy(false)
    }
  }

  const runCreateCorpus = async (e: FormEvent) => {
    e.preventDefault()
    if (!newSlug.trim() || !newName.trim()) return
    setBusy(true)
    setFormError(null)
    try {
      await addCorpus({ slug: newSlug.trim(), name: newName.trim() })
      setNewSlug('')
      setNewName('')
      setNewCorpusOpen(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'create_corpus_failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shell">
      <header className="top compact">
        <div className="brand">
          <span className="mark">BeyondQuota</span>
          <h1>Knowledge</h1>
        </div>
        <p className="lede tight">
          {loading
            ? '…'
            : `${activeCount} active · ${docs.length} total${
                corpus ? ` · ${corpus.slug}` : ''
              }`}
        </p>
      </header>

      <div className="controls todoist" role="toolbar" aria-label="Filters">
        {corpora.length > 0 ? (
          <div className="segs" role="group" aria-label="Corpus">
            {corpora.map((c) => (
              <button
                key={c.id}
                type="button"
                className={corpusId === c.id ? 'seg on' : 'seg'}
                aria-pressed={corpusId === c.id}
                onClick={() => selectCorpus(c.id)}
                title={c.name}
              >
                {c.name}
              </button>
            ))}
          </div>
        ) : null}

        <div className="segs" role="group" aria-label="Status">
          {STATUS_OPTS.map((s) => (
            <button
              key={s}
              type="button"
              className={status === s ? 'seg on' : 'seg'}
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <label className="ctl grow">
          <span>Search</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Title, slug…"
            spellCheck={false}
          />
        </label>

        <button
          type="button"
          className="ghost"
          onClick={() => {
            setIngestOpen((o) => !o)
            setNewCorpusOpen(false)
          }}
        >
          {ingestOpen ? 'Cancel' : 'Ingest'}
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => {
            setNewCorpusOpen((o) => !o)
            setIngestOpen(false)
          }}
        >
          {newCorpusOpen ? 'Cancel' : 'New corpus'}
        </button>
        <button
          type="button"
          className={loading || pending ? 'ghost refresh is-busy' : 'ghost refresh'}
          disabled={loading || pending}
          onClick={() => void refresh()}
        >
          {loading || pending ? '…' : 'Refresh'}
        </button>
      </div>

      {error || formError ? (
        <p className="banner" role="alert">
          {formError ?? error}
          <span className="hint">
            Start the local API (`./scripts/dev_operator_ui.sh`) and sign in.
          </span>
        </p>
      ) : null}

      {newCorpusOpen ? (
        <form className="k-inline-form" onSubmit={(e) => void runCreateCorpus(e)}>
          <label className="ctl grow">
            <span>Slug</span>
            <input
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="acme-product"
              required
            />
          </label>
          <label className="ctl grow">
            <span>Name</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Acme product docs"
              required
            />
          </label>
          <button type="submit" className="ghost" disabled={busy}>
            Create
          </button>
        </form>
      ) : null}

      {ingestOpen && corpusId ? (
        <form className="k-inline-form stack" onSubmit={(e) => void runIngest(e)}>
          <label className="ctl grow">
            <span>Slug</span>
            <input
              value={ingestSlug}
              onChange={(e) => setIngestSlug(e.target.value)}
              placeholder="pricing-faq"
              required
            />
          </label>
          <label className="ctl grow">
            <span>Markdown</span>
            <textarea
              value={ingestMd}
              onChange={(e) => setIngestMd(e.target.value)}
              rows={8}
              placeholder={'# Title\n\n## Section\n…'}
              required
            />
          </label>
          <label className="k-check">
            <input
              type="checkbox"
              checked={ingestActivate}
              onChange={(e) => setIngestActivate(e.target.checked)}
            />
            Activate after ingest
          </label>
          <button type="submit" className="ghost" disabled={busy}>
            Ingest
          </button>
        </form>
      ) : null}

      <main className={loading && !filtered.length ? 'list dim' : 'list'}>
        {loading && !docs.length ? (
          <p className="empty">Loading…</p>
        ) : !corpora.length ? (
          <p className="empty">
            No corpora yet. Seed is on by default (`KNOWLEDGE_SEED≠0`), or create one.
          </p>
        ) : filtered.length === 0 ? (
          <p className="empty">No documents for this filter.</p>
        ) : (
          <ul className="todo-list">
            {filtered.map((d) => (
              <li key={d.doc_id} className={`todo status-${d.status}`}>
                <span className="todo-disclose spacer" aria-hidden="true" />
                <Link to={`/knowledge/${d.doc_id}`} className="todo-body">
                  <span className="todo-title">{d.title || d.slug}</span>
                  {d.description ? (
                    <span className="todo-preview">
                      {d.description.slice(0, 140)}
                      {d.description.length > 140 ? '…' : ''}
                    </span>
                  ) : null}
                  <span className="todo-meta">
                    <span className={`pill status-pill status-${d.status}`}>
                      {d.status}
                    </span>
                    <span className="pill muted">{d.slug}</span>
                    <span className="pill muted">
                      {d.node_count} nodes · {d.line_count} lines
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  )
}
